import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link as LinkIcon, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// If you already have a groupFeedApi, import it and use it here.
// Otherwise, wire the vote call however you do API requests in your project.
import { groupFeedApi } from '../feedApi';

function normalizePoll(poll) {
  if (!poll) return null;
  const options = Array.isArray(poll.options) ? poll.options : [];
  const normalized = options.map((o, i) => {
    if (typeof o === 'string') {
      return { id: `opt_${i + 1}`, text: o, votes: 0 };
    }
    return {
      id: o.id || `opt_${i + 1}`,
      text: o.text ?? o.label ?? o.option ?? '',
      votes: Number.isFinite(o.votes) ? o.votes : 0,
    };
  });

  return {
    question: poll.question || '',
    multiple: !!poll.multiple,
    total_votes: Number.isFinite(poll.total_votes) ? poll.total_votes : undefined,
    options: normalized,
  };
}

export default function PostFeedItemCard({ groupId, item, onOpenExternal }) {
  const { t, i18n } = useTranslation();

  const url = item.article_url || item.external_url || '';
  const body = item.description ?? item.body ?? '';

  const [poll, setPoll] = useState(() => normalizePoll(item.poll));
  const [myVote, setMyVote] = useState([]); // backend returns array
  const [voting, setVoting] = useState(false);

  useEffect(() => {
    setPoll(normalizePoll(item.poll));
  }, [item.poll]);

  const createdLabel = useMemo(() => {
    if (!item.created_at) return '—';
    try {
      return new Date(item.created_at).toLocaleString(i18n.language || undefined);
    } catch {
      return new Date(item.created_at).toLocaleString();
    }
  }, [item.created_at, i18n.language]);

  const attachmentsCount = item.attachments?.length || 0;

  const openExternal = () => {
    if (!url) return;
    if (onOpenExternal) return onOpenExternal(url);
    window.open(url, '_blank', 'noreferrer');
  };

  const submitVote = async (optionId) => {
    if (!groupId || !item?.id || !poll?.options?.length) return;

    try {
      setVoting(true);

      // Backend: POST /groups/:groupId/feed/:feedId/poll/vote { optionId }
      const resp = await groupFeedApi.votePoll(groupId, item.id, { optionId });

      if (resp?.poll) setPoll(normalizePoll(resp.poll));
      if (Array.isArray(resp?.my_vote)) setMyVote(resp.my_vote);

      toast.success(t('groupSpace.feed.voteRecorded', { defaultValue: 'Vote recorded' }));
    } catch (err) {
      console.error(err);
      toast.error(t('groupSpace.feed.voteError', { defaultValue: 'Failed to submit vote' }));
    } finally {
      setVoting(false);
    }
  };

  return (
    <Card className="border border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base truncate">{item.title}</CardTitle>
            <CardDescription className="text-xs mt-1">{createdLabel}</CardDescription>
          </div>

          <div className="flex items-center gap-2 flex-wrap justify-end">
            {item.poll && (
              <Badge variant="outline">
                {t('groupSpace.feed.badge.poll', { defaultValue: 'Poll' })}
              </Badge>
            )}
            {!!url && (
              <Badge variant="outline">
                {t('groupSpace.feed.badge.article', { defaultValue: 'Article' })}
              </Badge>
            )}
            {!!attachmentsCount && (
              <Badge variant="outline">
                {t('groupSpace.feed.badge.files', {
                  count: attachmentsCount,
                  defaultValue: `${attachmentsCount} file(s)`,
                })}
              </Badge>
            )}
            {!!item.is_pinned && (
              <Badge>
                {t('groupSpace.feed.badge.pinned', { defaultValue: 'Pinned' })}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {item.image_url && (
          <img
            src={item.image_url}
            alt={item.title}
            className="w-full max-h-60 object-cover rounded-lg"
          />
        )}

        {body && <p className="text-sm text-slate-700 whitespace-pre-line">{body}</p>}

        {!!url && (
          <Button
            type="button"
            variant="link"
            className="px-0 h-auto inline-flex items-center text-sm"
            onClick={openExternal}
          >
            <LinkIcon size={14} className="mr-1" />
            {t('groupSpace.feed.openArticle', { defaultValue: 'Open article' })}
          </Button>
        )}

        {poll && poll.question && poll.options?.length >= 2 && (
          <div className="border rounded-lg p-3 bg-slate-50">
            <div className="flex items-center justify-between gap-3 mb-2">
              <p className="text-xs font-semibold tracking-wide text-slate-600 uppercase">
                {t('groupSpace.feed.badge.poll', { defaultValue: 'Poll' })}
              </p>
              {voting && (
                <span className="text-xs text-slate-500">
                  {t('groupSpace.feed.course.loading', { defaultValue: 'Loading…' })}
                </span>
              )}
            </div>

            <p className="text-sm font-medium">{poll.question}</p>
            <p className="text-xs text-slate-500 mt-1">
              {t('groupSpace.feed.poll.hintSingle', { defaultValue: 'Choose one option to vote.' })}
            </p>

            <div className="mt-3 space-y-2">
              {poll.options.map((opt) => {
                const selected = myVote.includes(opt.id);

                return (
                  <button
                    key={opt.id}
                    type="button"
                    disabled={voting}
                    onClick={() => submitVote(opt.id)}
                    className={`w-full flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition
                      ${selected ? 'border-slate-900 bg-white' : 'bg-white hover:bg-slate-50'}
                      ${voting ? 'opacity-60 cursor-not-allowed' : ''}
                    `}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div
                        className={`h-4 w-4 rounded-full border flex items-center justify-center ${
                          selected ? 'border-slate-900' : 'border-slate-300'
                        }`}
                      >
                        {selected ? <div className="h-2 w-2 rounded-full bg-slate-900" /> : null}
                      </div>
                      <span className="text-sm text-slate-800 truncate">{opt.text}</span>
                    </div>

                    <span className="text-xs text-slate-500 shrink-0">
                      {t('groupSpace.feed.votes', { count: opt.votes || 0, defaultValue: `${opt.votes || 0} vote(s)` })}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!!attachmentsCount && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-slate-500">
              {t('groupSpace.feed.attachmentsLabel', { defaultValue: 'Attachments' })}
            </p>
            <ul className="space-y-1">
              {item.attachments.map((att) => (
                <li key={att.id || att.url}>
                  <a
                    href={att.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center text-xs text-blue-600 hover:underline"
                  >
                    <FileText size={12} className="mr-1" />
                    {att.name || t('groupSpace.feed.downloadAttachment', { defaultValue: 'Download file' })}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
